// Our trucks for a run, one row each — the 26 ft trucks first (T1, T2, …), then
// the 53 ft ones — with when each leaves the depot, then each 3PL truck offered
// (3PL1, 3PL2, …) with when it comes. Shared by the order picker and the CSV
// upload; the run sends the times in that truck order.

import { useState, type CSSProperties } from "react";

import { formatClock } from "../lib/format";
import { useT } from "../lib/i18n";

// A truck leaves at 8:30 unless the dispatcher says otherwise (loading starts
// an hour earlier).
export const DEFAULT_DEPARTURE_MIN = 8 * 60 + 30;

// 5:00 AM to 2:00 PM on the quarter hour, in minutes from midnight: the
// departures the backend accepts.
export const DEPARTURE_SLOTS = Array.from({ length: 37 }, (_, i) => 5 * 60 + i * 15);

const MAX_26FT = 10;
const MAX_53FT = 3;
const MAX_3PL = 4;

// Each size keeps its own trucks' times, so changing how many 26 ft trucks
// there are never moves a 53 ft truck's departure.
export function useTruckDepartures() {
  const fill = (saved: number[] | undefined, n: number) =>
    Array.from({ length: n }, (_, i) => saved?.[i] ?? DEFAULT_DEPARTURE_MIN);
  const [dep26, setDep26] = useState(() => fill(undefined, MAX_26FT));
  const [dep53, setDep53] = useState(() => fill(undefined, MAX_53FT));
  const [dep3pl, setDep3pl] = useState(() => fill(undefined, MAX_3PL));
  return {
    dep26,
    dep53,
    dep3pl,
    set(size: "26ft" | "53ft" | "3pl", i: number, min: number) {
      const update = (prev: number[]) => prev.map((m, j) => (j === i ? min : m));
      if (size === "26ft") setDep26(update);
      else if (size === "53ft") setDep53(update);
      else setDep3pl(update);
    },
    restore(saved: { dep26?: number[]; dep53?: number[]; dep3pl?: number[] }) {
      setDep26(fill(saved.dep26, MAX_26FT));
      setDep53(fill(saved.dep53, MAX_53FT));
      setDep3pl(fill(saved.dep3pl, MAX_3PL));
    },
    // The run's departures, one per truck in truck order.
    list(num26ft: number, num53ft: number): number[] {
      return [...dep26.slice(0, num26ft), ...dep53.slice(0, num53ft)];
    },
  };
}

export type TruckDepartureState = ReturnType<typeof useTruckDepartures>;

export function TruckDepartures({
  num26ft,
  num53ft,
  num3pl,
  departures,
}: {
  num26ft: number;
  num53ft: number;
  num3pl: number;
  departures: TruckDepartureState;
}) {
  const t = useT();
  const rows = [
    ...departures.dep26.slice(0, num26ft).map((min, i) => ({ size: "26ft" as const, i, min })),
    ...departures.dep53.slice(0, num53ft).map((min, i) => ({ size: "53ft" as const, i, min })),
    ...departures.dep3pl.slice(0, num3pl).map((min, i) => ({ size: "3pl" as const, i, min })),
  ];
  return (
    <div style={list}>
      {rows.map(({ size, i, min }, n) => {
        const truck =
          size === "3pl"
            ? `3PL${i + 1}`
            : t("fleet.truckOf", {
                truck: `T${n + 1}`,
                size: t(size === "26ft" ? "fleet.26ft" : "fleet.53ft"),
              });
        return (
          <label key={`${size}-${i}`} style={row}>
            <span style={label}>{truck}</span>
            <select
              aria-label={t("fleet.departsOf", { truck })}
              style={select}
              value={min}
              onChange={(e) => departures.set(size, i, Number(e.target.value))}
            >
              {DEPARTURE_SLOTS.map((m) => (
                <option key={m} value={m}>
                  {formatClock(m)}
                </option>
              ))}
            </select>
          </label>
        );
      })}
    </div>
  );
}

const list: CSSProperties = { display: "grid", gap: 4, marginTop: 8 };
const row: CSSProperties = { display: "flex", alignItems: "center", gap: 8 };
const label: CSSProperties = {
  flex: 1,
  fontSize: 12,
  fontWeight: 600,
  color: "var(--muted)",
  whiteSpace: "nowrap",
};
const select: CSSProperties = {
  padding: "4px 6px",
  border: "1px solid var(--border-strong)",
  borderRadius: 6,
  background: "var(--surface)",
  fontSize: 13,
};
